/**
 * Banner Management Type Definitions
 * Based on docs/BANNER_AND_TRAILER_API_DOCUMENTATION.md
 */

export type BannerLinkType =
  | 'NONE'
  | 'EXTERNAL'
  | 'CONTENT'
  | 'SUBSCRIPTION_PLAN'
  | 'GENRE';

export type TargetAudience =
  | 'ALL'
  | 'FREE_ONLY'
  | 'PREMIUM_ONLY'
  | 'VIP_ONLY';

export interface Banner {
  bannerId: string;
  title: string;
  description: string | null;
  imageUrl: string;
  altText: string | null;
  linkType: BannerLinkType;
  linkUrl: string | null;
  targetContentId: string | null;
  targetPlanId: string | null;
  targetGenreId: string | null;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  startDate: string | null;
  endDate: string | null;
  targetAudience: TargetAudience;
  createdAt: string;
  updatedAt: string;
  deactivatedAt: string | null;
}

export interface BannerCreateRequest {
  title: string;
  description?: string;
  imageUrl?: string;
  altText?: string;
  linkType?: BannerLinkType;
  linkUrl?: string | null;
  targetContentId?: string | null;
  targetPlanId?: string | null;
  targetGenreId?: string | null;
  isActive?: boolean;
  isFeatured?: boolean;
  displayOrder?: number;
  startDate?: string | null;
  endDate?: string | null;
  targetAudience?: TargetAudience;
}

export interface BannerUpdateRequest {
  title?: string;
  description?: string | null;
  imageUrl?: string | null;
  altText?: string | null;
  linkType?: BannerLinkType;
  linkUrl?: string | null;
  targetContentId?: string | null;
  targetPlanId?: string | null;
  targetGenreId?: string | null;
  isActive?: boolean;
  isFeatured?: boolean;
  displayOrder?: number;
  startDate?: string | null;
  endDate?: string | null;
  targetAudience?: TargetAudience;
}

export interface BannerQueryParams {
  page?: number;
  size?: number;
  isActive?: boolean;
  isFeatured?: boolean;
}

export interface BannersData {
  items: Banner[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export const BANNER_LINK_TYPES: { value: BannerLinkType; label: string }[] = [
  { value: 'NONE', label: 'None' },
  { value: 'EXTERNAL', label: 'External URL' },
  { value: 'CONTENT', label: 'Content' },
  { value: 'SUBSCRIPTION_PLAN', label: 'Subscription Plan' },
  { value: 'GENRE', label: 'Genre' },
];

export const TARGET_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'ALL', label: 'All Users' },
  { value: 'FREE_ONLY', label: 'Free Only' },
  { value: 'PREMIUM_ONLY', label: 'Premium Only' },
  { value: 'VIP_ONLY', label: 'VIP Only' },
];

export const formatLinkType = (type: BannerLinkType): string => {
  switch (type) {
    case 'NONE':
      return 'None';
    case 'EXTERNAL':
      return 'External URL';
    case 'CONTENT':
      return 'Content';
    case 'SUBSCRIPTION_PLAN':
      return 'Subscription Plan';
    case 'GENRE':
      return 'Genre';
    default:
      return type;
  }
};

export const formatTargetAudience = (audience: TargetAudience): string => {
  switch (audience) {
    case 'ALL':
      return 'All Users';
    case 'FREE_ONLY':
      return 'Free Users';
    case 'PREMIUM_ONLY':
      return 'Premium Users';
    case 'VIP_ONLY':
      return 'VIP Users';
    default:
      return audience;
  }
};

export const getLinkTypeBadgeColor = (type: BannerLinkType): string => {
  switch (type) {
    case 'NONE':
      return 'bg-gray-100 text-gray-800';
    case 'EXTERNAL':
      return 'bg-blue-100 text-blue-800';
    case 'CONTENT':
      return 'bg-indigo-100 text-indigo-800';
    case 'SUBSCRIPTION_PLAN':
      return 'bg-green-100 text-green-800';
    case 'GENRE':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getTargetAudienceBadgeColor = (audience: TargetAudience): string => {
  switch (audience) {
    case 'ALL':
      return 'bg-gray-100 text-gray-800';
    case 'FREE_ONLY':
      return 'bg-blue-100 text-blue-800';
    case 'PREMIUM_ONLY':
      return 'bg-yellow-100 text-yellow-800';
    case 'VIP_ONLY':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

